import { ChevronsUpDown } from "lucide-react";
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type ContextHeaderTriggerProps = Omit<
  ComponentPropsWithoutRef<typeof Button>,
  "children" | "variant" | "size"
> & {
  title: string;
  subtitle?: ReactNode;
  icon?: ReactNode;
  "aria-label": string;
};

/** Shared chrome for sidebar context header (account / brand / app). Occupies Logo slot (~h-14). */
export const ContextHeaderTrigger = forwardRef<HTMLButtonElement, ContextHeaderTriggerProps>(
  function ContextHeaderTrigger(
    { title, subtitle, icon, "aria-label": ariaLabel, className, ...props },
    ref,
  ) {
    return (
      <Button
        ref={ref}
        variant="ghost"
        className={cn(
          "h-14 w-full justify-between gap-2.5 rounded-xl px-2 py-2 text-left whitespace-normal",
          "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
          "aria-expanded:bg-sidebar-accent aria-expanded:text-sidebar-accent-foreground",
          className,
        )}
        aria-label={ariaLabel}
        {...props}
      >
        {icon}
        <span className="min-w-0 flex-1 space-y-0.5">
          <span className="block truncate font-heading text-[15px] font-medium leading-snug tracking-tight">
            {title}
          </span>
          {subtitle ? (
            <span className="block truncate font-heading text-xs leading-snug text-muted-foreground">
              {subtitle}
            </span>
          ) : null}
        </span>
        <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground/45" aria-hidden />
      </Button>
    );
  },
);
